import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  EventEmitter,
  HostListener,
  Input,
  IterableDiffers,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {DataViewColumn, DataViewRow, DataViewRowFormattingOptions} from '../../../../../../../../models/dataview';
import {ContextMenuDocument} from './cell/context-menu';
import {Footnote} from '../../../../../../../../models/footnote';
import {DocumentFileService} from '../../../../../../../../services/document-file.service';
import {Observable, Subscription} from 'rxjs';
import {AutoUnsubscribe} from '../../../../../../../../decorators/auto-unsubscribe';
import {Router} from '@angular/router';
import {LaunchDarklyService} from '@services/launchdarkly.service';
import {StepLabelService} from '@services/step-label.service';
import {NumberFormattingService} from '@services/number-formatting.service';
import {SCENARIO_TYPE_PROFORMA, SCENARIO_TYPE_PROJECTION} from '@utils/constants';
import {CommonFunctions} from '@utils/common-functions';
import {StatementService} from '@services/statement.service';
import {ProjectionHelperService} from '@services/projection-helper.service';
import {CellInformationService} from '@services/shared/cell-information/cell-information.service';
import {DataFrameValue} from '@models/dataframe';

declare var bootstrap: any;

@Component({
    selector: 'app-financials-table',
    templateUrl: './financials-table.component.html',
    styleUrls: ['./financials-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
@AutoUnsubscribe('subsArr$')
export class FinancialsTableComponent implements OnChanges, DoCheck, OnInit, AfterViewInit {
  subsArr$: Subscription[] = [];
  @Input() roundToThousands: boolean;
  @Input() multiply_by_factor: string;
  @Input() rows: Array<DataViewRow> = [];
  @Input() columns: Array<DataViewColumn> = [];
  @Input() hiddenRowIndices: Set<number>;
  @Input() hiddenColumnIndices: Set<string>;
  @Input() showNumbers: boolean;
  @Input() showCommonSize: boolean;
  @Input() showTrend: boolean;
  @Input() showSourceItems: boolean;
  @Output() rowToggled = new EventEmitter<number>();
  @Output() toggleRowVisibility = new EventEmitter<number>();
  @Output() columnHidden = new EventEmitter<string>();
  @Input() documentMenuItems: Array<ContextMenuDocument> = [];
  @Input() allowEditCells: boolean;
  @Input() showSpreadingInContextMenu = true;
  @Input() showHidden: boolean;
  @Input() dollarSign: boolean;
  @Input() statementType: string;
  @Input() companyId: number;
  @Input() templated: boolean;
  @Input() projectionScenarioTypeLabelOverride = 'Scenario';
  @Input() viewOnlyMode: boolean;
  @Input() embeddedMode = false;
  @Input() commonSizeDecimalPrecision = 1;
  @Input() insightHighlightRefs: Set<string> = new Set();
  @Input() insightHighlightColumnIndex: number = -1;
  @Input() insightSelectedRowRef: string = null;
  @Input() insightsLoadingComplete$: Observable<void>;
  @Output() openFootnotes = new EventEmitter<Footnote>();
  @Output() openInsights = new EventEmitter<any>();
  @Output() updateInsightSource = new EventEmitter<any>();
  @Output() beginDeleteColumn = new EventEmitter<DataViewColumn>();
  @Output() beginEditColumn = new EventEmitter<DataViewColumn>();
  @Output() beginDuplicateColumn = new EventEmitter<DataViewColumn>();
  @Output() openCommentView = new EventEmitter<DataViewColumn>();
  @Output() openCellCommentView = new EventEmitter<{cellInformation: any, cellTitle: string, cellStatementDate: string}>();

  selectedRow = -1;
  selectedColumn = -1;
  displayRows: Array<DataViewRow> = [];
  iterableDiffer: any;
  showToolTip;
  toolTipPixelOffset = 120;
  isSourceStatement = false;
  allowHideZeros = false;
  excludedCellTooltipText: string = "This item is grayed out because it was excluded during the Data Categorization step and does not affect calculations.";
  sectionDrawerState: { [key: number]: boolean } = {};
  lineItemIdToParentSectionIdMapping: Map<number, number> = new Map<number, number>();
  contextMenuOpenColumnIdx: number = -1;
  contextMenuPosition: { top: number; left: number } = { top: 0, left: 0 };
  highlightedCellMap: {[key: string]: boolean} = {};
  selectedCellMap: {[key: string]: boolean} = {};

  constructor(
    private _documentFileService: DocumentFileService,
    private _iterableDiffers: IterableDiffers,
    private _router: Router,
    private _changeDetector: ChangeDetectorRef,
    private _featureFlags: LaunchDarklyService,
    private _numberFormattingService: NumberFormattingService,
    private _statementService: StatementService,
    public _projectionHelperService: ProjectionHelperService,
    private cellInformationService: CellInformationService,
    private _stepLabelService: StepLabelService,
  ) {
    this.iterableDiffer = this._iterableDiffers.find([]).create(null);
  }

  // Stop the highlighting when we click outside the element
  @HostListener('document:click', ['$event'])
  clickout(event) {
    this.selectedRow = -1;
    this.selectedColumn = -1;

    // unless the click event was on a cell-menu-toggle-icon, close column-level context menu
    if (!CommonFunctions.hasClassNameInHierarchy(event.target as HTMLElement, 'column-menu-toggle-icon')) {
      this.contextMenuOpenColumnIdx = -1;
    }
  }

  ngOnInit(): void {
    this.isSourceStatement = this.isASourceStatement();

    this.subsArr$.push(this._featureFlags.flagChange.subscribe(() => {
      this.setFlags();
    }));

    this.setFlags();

    this.initializeSectionDrawerStatesAndLineItemIdToParentSectionIdMappings();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.hasOwnProperty('rows') || changes.hasOwnProperty('columns') ||
      changes.hasOwnProperty('multiply_by_factor') || changes.hasOwnProperty('documentMenuItems') ||
      changes.hasOwnProperty('showNumbers') || changes.hasOwnProperty('showCommonSize') ||
      changes.hasOwnProperty('showTrend') || changes.hasOwnProperty('dollarSign') ||
      changes.hasOwnProperty('showSourceItems')) {
      if (changes.hasOwnProperty('rows')) {
        this.initializeSectionDrawerStatesAndLineItemIdToParentSectionIdMappings();
      }
      this.prerenderData();
    }
    
    if (changes.hasOwnProperty('insightHighlightRefs') || 
        changes.hasOwnProperty('insightHighlightColumnIndex') ||
        changes.hasOwnProperty('insightSelectedRowRef') ||
        changes.hasOwnProperty('rows')) {
      this.computeHighlightedCells();
    }
  }

  ngDoCheck() {
    const changes = this.iterableDiffer.diff(this.hiddenColumnIndices);
    if (changes) {
      this.setHiddenColumns();
    }
  }

  ngAfterViewInit() {
    setTimeout(this.enableTooltips, 2000);
  }

  enableTooltips() {
    // Bootstrap tooltip initialization
    if (typeof bootstrap === 'undefined' || !bootstrap.Tooltip) {
      return;
    }
    const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
    tooltipTriggerList.forEach(tooltipTriggerEl => {
      return bootstrap.Tooltip.getOrCreateInstance(tooltipTriggerEl)
    })
  }

  showTip(index) {
    this.showToolTip = true;
    this._changeDetector.detectChanges();
    const icon = document.getElementById('tipIcon' + index);
    const coord = icon.getBoundingClientRect();
    const tip = document.getElementById('tip');
    tip.setAttribute('style', 'left:' + (coord.left - this.toolTipPixelOffset) + 'px;');
  }

  hideTip() {
    this.showToolTip = false;
  }

  setHiddenColumns() {
    for (let j = 0; j < this.columns.length; j++) {
      const keyString = this.columns[j].toKeyString();
      if (this.hiddenColumnIndices.has(keyString)) {
        this.columns[j].isHidden = true;
      } else {
        this.columns[j].isHidden = false;
      }
    }
  }

  /**
   * Add attributes the cells, rows, columns, etc so we don't have to figure them out every time we render the view
   */
  prerenderData() {
    if (this.allowHideZeros) {
      this.runHiddenRowRules();
    }
    this.columns.forEach((col, colIdx) => {
      col.documentOptions = this.documentOptionsForColumn(col);
      if ((col.cellFormat.textFormat === 'PERCENT') ||
        (this.hiddenColumnIndices.has(col.toKeyString()))) {
        col.isHidden = true;
      } else {
        col.isHidden = false;
      }
      col.trendIsHidden = !this.showTrend;
      col.commonSizeIsHidden = !this.showCommonSize;

      col.cells.forEach((cell, cellIdx) => {
        const row = this.rows[cellIdx];
        cell.formattedValue = this.formatItem(row, col, cellIdx, true);

        if ((col.cellFormat.textFormat === 'PERCENT') ||
          (this.hiddenColumnIndices.has(col.toKeyString()))) {
          cell.isHidden = true;
        } else {
          cell.isHidden = false;
        }
        cell.documentOptions = this.documentOptionsForCell(cell, col);
      })
    });
    setTimeout(() => this.enableTooltips(), 0);
  }

  initializeSectionDrawerStatesAndLineItemIdToParentSectionIdMappings() {
    let currentSectionLineItemId = -1;
    let previousHeaderOriginalIndentation = -1;
    this.rows.forEach(row => {
      if (row.rowFormat && row.rowFormat.header) {
        const originalIndentation = row.indentation;
        const hasParentSection = currentSectionLineItemId !== -1;
        const isRootLevel = originalIndentation === -1 || originalIndentation === 0;
        const previousWasRootLevel = previousHeaderOriginalIndentation === -1 || previousHeaderOriginalIndentation === 0;
        
        if (isRootLevel && previousWasRootLevel && hasParentSection) {
          currentSectionLineItemId = -1;
        }
        
        const isSecondLevelHeader = hasParentSection && (originalIndentation === 0 || originalIndentation === 1) && !(isRootLevel && previousWasRootLevel);
        
        if (isSecondLevelHeader) {
          row.indentation = 0;
          row.rowFormat.isParent = false;
        } else {
          row.indentation = -1;
          if (row.lineItem) {
            this.sectionDrawerState[row.lineItem.id] = true;
            currentSectionLineItemId = row.lineItem.id;
          }
        }
        previousHeaderOriginalIndentation = originalIndentation;
      } else if (row.rowFormat && row.rowFormat.isParent && row.indentation === 1) {
        row.indentation = 0;
        row.rowFormat.isParent = false;
      }
      // Source-item rows (shown via the Source Items toggle) aren't tied to a template lineItem.
      if (row.lineItem) {
        this.lineItemIdToParentSectionIdMapping[row.lineItem.id] = currentSectionLineItemId;
      }
    })
  }

  formatItem(row: DataViewRow, column: DataViewColumn, idx: number, showOriginal = false): string {
    const value = column.cells[idx].calculatedValue;
    if (this.isHeaderRow(row)) {
      return '';
    }
    if (value) {

      if (!!value.error) {
        switch (value.error) {
          case '#REF':
            if (row.cellFormat.replaceRef) {
              return row.cellFormat.replaceRef;
            } else if (column.cellFormat.replaceRef) {
              return column.cellFormat.replaceRef;
            }
            return '#REF';
          case '#DIV/0':
            if (row.cellFormat.replaceDivzero) {
              return row.cellFormat.replaceDivzero;
            } else if (column.cellFormat.replaceDivzero) {
              return column.cellFormat.replaceDivzero;
            }
            return '#DIV/0';
          default:
            return '#ERR';
        }
      }

      let textFormat = column.cellFormat.textFormat;
      const numDecimals = row.rowFormat.numDecimals;

      if (!textFormat) {
        textFormat = row.cellFormat.textFormat;
      }
      const excludeFromRoundingAndMultiplication = row.excludeFromRoundingAndMultiplication
      const options = { showDollarSign: this.dollarSign, currency: column.currencyFormat, multiply_by_factor: excludeFromRoundingAndMultiplication ? 'ACTUALS' : this.multiply_by_factor, rows: this.rows };
      return this._numberFormattingService.formatNumber(value.value, textFormat, numDecimals, options);
    }
    return '-';
  }

  getPercent(column: DataViewColumn, idx: number, isHeader: boolean, cellPropertyName: string, decimalPrecision = 1) {
    if (isHeader) {
      return '';
    }
    const cell = column.cells[idx];
    if (cell && cell[cellPropertyName] && cell[cellPropertyName].value !== null && cell[cellPropertyName].value !== undefined) {
      return (cell[cellPropertyName].value * 100).toFixed(decimalPrecision).toString() + '%';
    } else {
      return '-';
    }
  }

  getTrend(column: DataViewColumn, idx: number, isHeader: boolean) {
    return this.getPercent(column, idx, isHeader, 'trendValue');
  }

  getCommonSize(column: DataViewColumn, idx: number, isHeader: boolean) {
    return this.getPercent(column, idx, isHeader, 'commonSizedValue', this.commonSizeDecimalPrecision);
  }

  runHiddenRowRules(): void {
    this.rows.forEach((row, rowIdx) => {
      let wasNonZeroFound = false;
      this.columns.forEach((col) => {
        // A column can briefly hold fewer cells than there are rows - e.g. right after a
        // document delete triggers a batch merge (mergeDataView()) that replaces columns
        // without refreshing rows - so cells[rowIdx] isn't guaranteed to exist.
        const cell = col.cells[rowIdx];
        if (cell?.calculatedValue && cell.calculatedValue.value !== 0) {
          wasNonZeroFound = true;
        }
      });

      if (
        !wasNonZeroFound &&
        row.rowFormat &&
        !row.rowFormat.header &&
        (row.rowFormat.valueSource !== 'CalculatedItem') &&
        !row.rowFormat?.isParent
      ) { // if all zeros and not bold and not a calculation, hide due to zeros
        this.rows[rowIdx].rowFormat['isHiddenDueToZeros'] = true;
      } else {
        this.rows[rowIdx].rowFormat['isHiddenDueToZeros'] = false;
      }
    });
  }

  // making sure item has expected value to not blow up
  isItemAvailable(column: DataViewColumn, idx: number): boolean {
    if (idx >= 0 && column && column.cells &&
      column.cells[idx] && column.cells[idx].calculatedValue) {
      return true;
    }
    return false;
  }

  itemFootnote(column: DataViewColumn, idx: number) {
    let value = null;
    if (this.isItemAvailable(column, idx)) {
      const possibleValue = column.cells[idx].calculatedValue.footnotes
      if (possibleValue && possibleValue.length > 0) {
        value = possibleValue;
      }
    }

    return value;
  }

  itemValue(column: DataViewColumn, idx: number) {
    let value;
    if (this.isItemAvailable(column, idx)) {
      value = column.cells[idx].calculatedValue.value;
    } else {
      value = 0;
    }
    return value;
  }

  originalItemValue(column: DataViewColumn, idx: number) {
    let value;
    if (this.isItemAvailable(column, idx)) {
      value = column.cells[idx].calculatedValue.originalValue;
    } else {
      value = 0;
    }
    return value;
  }

  selectedCell(rowIdx: number, colIdx: number) {
    this.selectedRow = rowIdx;
    this.selectedColumn = colIdx;
    this.contextMenuOpenColumnIdx = -1;
    this.updateInsightSelection()
  }

  toggleSectionDrawerState(lineItemId: number) {
    this.sectionDrawerState[lineItemId] = !this.sectionDrawerState[lineItemId];
    this._changeDetector.markForCheck();
  }

  getClassForSectionToggle(lineItemId: number) {
    if (this.isSourceStatement) {
      return '';
    }
    const row = this.rows.find(r => r.lineItem?.id === lineItemId);
    if (!row) {
      return '';
    }
    
    const rowFormat = this.getRowFormatClass(row.rowFormat, this.rows.indexOf(row));
    if (rowFormat.includes('header-second-level')) {
      return '';
    }
    for (const childId in this.lineItemIdToParentSectionIdMapping) {
      if (this.lineItemIdToParentSectionIdMapping[childId] === lineItemId && Number(childId) !== lineItemId) {
        return this.sectionDrawerState[lineItemId] ? 'fas fa-chevron-down' : 'fas fa-chevron-right';
      }
    }
    return '';
  }

  isHeaderRow(row: DataViewRow): boolean {
    if (!row.rowFormat) {
      return false;
    }
    if (row.rowFormat.header && row.indentation === -1) {
      return true;
    }
    if (row.indentation === 0 && (row.rowFormat.header || row.rowFormat.isParent)) {
      return true;
    }
    return false;
  }

  getRowFormatClass(rowFormat: DataViewRowFormattingOptions, idx: number): string {
    if (rowFormat.isHiddenDueToZeros) {
      return 'hidden';
    }


    if (this.hiddenRowIndices.has(idx)) {
      if (this.showHidden) {
        return 'grayed';
      } else {
        return 'hidden';
      }
    }

    if (!rowFormat) {
      return '';
    }
    // CITI-717
    const row = this.rows[idx];
    const isFirstLevelHeader = rowFormat.header && row && row.indentation === -1;
    const isSourceItemRow = row && row.isSourceItemRow;
    const isSecondLevelHeader =
      row &&
      (row.indentation === 0 && (rowFormat.header || rowFormat.isParent));
    const isUnderSecondLevelHeader = (() => {
      for (let i = idx - 1; i >= 0; i--) {
        const above = this.rows[i];
        if (above.indentation === -1 && above.rowFormat?.header) {
          return false;
        }
        if ((above.rowFormat?.header || above.rowFormat?.isParent)) {
          return above.indentation === 0;
        }
      }
      return false;
    })();
      
    let classes: string[] = [];
    if (isFirstLevelHeader) {
      classes.push(rowFormat.bold ? 'header bold header-first-level' : 'header header-first-level');
    } else if (isSecondLevelHeader) {
      classes.push('header header-second-level');
    } else if (rowFormat.header && rowFormat.bold) {
      classes.push('header bold');
    } else if (rowFormat.header) {
      classes.push('header');
    } else if (rowFormat.bold) {
      classes.push('bold');
    }
    if (isSourceItemRow) {
      classes.push('source-item-row');
    }
    if (isUnderSecondLevelHeader && !rowFormat.header) {
      classes.push('under-header-second-level');
    }    

    return classes.join(' ');
  }

  toggleRow(evt = null, idx: number) {
    if (evt) {
      evt.stopPropagation();
      evt.preventDefault();
    }
    this.rowToggled.emit(idx);
  }

  hideRow(evt = null, idx: number) {
    if (evt) {
      evt.stopPropagation();
      evt.preventDefault();
    }
    this.toggleRowVisibility.emit(idx);
  }

  hideColumn(evt = null, idx: number) {
    if (evt) {
      evt.stopPropagation();
      evt.preventDefault();
      this.contextMenuOpenColumnIdx = -1;
    }
    const idxStr = this.columns[idx].toKeyString();
    this.columnHidden.emit(idxStr);
  }

  selectCell(row: DataViewRow, column: DataViewColumn, rowIdx: number, colIdx: number) {
    if (this.selectedRow === rowIdx && this.selectedColumn === colIdx) {
      this.selectedRow = -1;
      this.selectedColumn = -1;
      return;
    }

    this.selectedRow = rowIdx;
    this.selectedColumn = colIdx;
    this.contextMenuOpenColumnIdx = -1;
    return;
  }
  
  _getInsightPayload() {
    return {
      reportingInterval: this.columns[this.selectedColumn].reportingInterval,
      statementDate: this.columns[this.selectedColumn].statementDate,
      preparationType: this.columns[this.selectedColumn].preparationType,
      lineItemRef: 'REF_' + this.rows[this.selectedRow].ref,
      companyId: this.companyId,
      scenario: this.columns[this.selectedColumn].scenario,
      projectionName: this.columns[this.selectedColumn].projectionName,
      currencyFormat: this.columns[this.selectedColumn].currencyFormat
    }
  }
  updateInsightSelection() {
    const payload = this._getInsightPayload();
    this.updateInsightSource.emit(payload);
  }
  
  insightsSelected(evt) {
    const payload = this._getInsightPayload()
    this.openInsights.emit(payload);
  }

  deletePeriodColumn(column: DataViewColumn) {
    this.beginDeleteColumn.emit(column);
  }

  editPeriodColumn(column: DataViewColumn){
    this.beginEditColumn.emit(column)
  }

  duplicateForecastedPeriod(column: DataViewColumn){
    this.beginDuplicateColumn.emit(column)
  }

  // filters the documentOptions for a particular column
  // a column could in theory have up to 3 docs associates with it (1 per statement type)
  documentOptionsForColumn(column: DataViewColumn) {
    return this.documentMenuItems.filter(i => column.fileIds.includes(i.id));
  }

  /*
    filters the documentOptions for a cell (in the context menu at the cell level, we only want docs from which that cell was derived from to appear)
    to do this we first get the sourceStatementId for the cell
    then, we iterate through the documentOptions search for ones that that have associated statements that match the cell source statement
    if we do not find any documentOptions with matching sourceStatementIds, we will just return all the documentOptions for the column
  */
  documentOptionsForCell(cell, col) {
    if (this.isSourceStatement) {
      return col.documentOptions;
    }
    const fileIds = [];
    const calculatedValue = cell.calculatedValue;
    if (calculatedValue !== null && calculatedValue.sourceStatementId !== 0) {
      const sourceStatementId = calculatedValue.sourceStatementId;
      for (let statementOption of col.documentOptions) {
        if (statementOption.associatedStatements.includes(sourceStatementId)) {
          fileIds.push(statementOption.id);
        }
      }
    }
    const filteredOptions = this.documentMenuItems.filter(i => fileIds.includes(i.id));
    return filteredOptions.length < 1 ? [] : filteredOptions;
  }

  columnLevelContextMenuDocumentClicked(doc: ContextMenuDocument, evt, action) {
    doc.action = action;
    evt.preventDefault();
    this.contextMenuOpenColumnIdx = -1;
    this.contextMenuItemSelected(doc);
  }

  contextMenuItemSelected(doc: ContextMenuDocument) {
    // Open the download link in a new window
    const docFileId = doc.id; // statement.id is a weirdly named variable (and incorrect).
    if (doc.action === 'download') {
      this.subsArr$.push(this._documentFileService.getDownloadUrlForOriginalDocument(doc.uuid).subscribe(data => {
        const url = data.originalFileUrl;
        const link = document.createElement('a');
        link.href = url;
        link.download = doc.label;
        link.target = '_blank';
        link.dispatchEvent(new MouseEvent('click'));
      }));
    }
    // Bring to spreading screen for this statement
    if (doc.action === 'spread') {
      if (
        this.selectedColumn === -1 ||
        this.selectedRow === -1 ||
        this.columns[this.selectedColumn].cells[this.selectedRow].calculatedValue === null ||
        this.columns[this.selectedColumn].cells[this.selectedRow].calculatedValue.sourceStatementId === 0
      ) {
        this._statementService.loadStatementsByDocFileId(docFileId).subscribe(returnedStatement => {
          this._router.navigate(['statements', returnedStatement.uuid, 'edit']);
        })
      } else {
        const statementId = this.columns[this.selectedColumn].cells[this.selectedRow].calculatedValue.sourceStatementId;
        this._statementService.loadStatement(statementId).subscribe(returnedStatement => {
          this._router.navigateByUrl('/statements/' + returnedStatement.uuid + '/edit', {
            replaceUrl: true,
          });
        });
      }
    }
  }

  isASourceStatement(): boolean {
    return this.statementType === 'Source Income Statement' || this.statementType === 'Source Balance Sheet' || this.statementType === 'Source Cash Flow Statement';
  }

  enterFootnotesMode(event: Footnote): void {
    this.openFootnotes.emit(event);
  }

  setFlags() {
    this.allowHideZeros = this._featureFlags.flags['hide-zeros-in-financials'];
    const spreadStep = this._stepLabelService.getStepLabel('SPREAD_FORMAL');
    this.excludedCellTooltipText = `This item is grayed out because it was excluded during the ${spreadStep} step and does not affect calculations.`;
    this.prerenderData();
  }

  trackColumn(index, column) {
    // By identity, not index - prepending older columns shifts every index.
    return column.toKeyString();
  }

  columnIsProjection(column: DataViewColumn): boolean {
    return column.scenario === SCENARIO_TYPE_PROJECTION || column.scenario === SCENARIO_TYPE_PROFORMA;
  }

  parentItemIsHidden(row: DataViewRow) {
    let aboveRow = false;
    for (let i = this.rows.length - 1; i >= 0; i--) {
      // Match by lineItem.id when the row has one (existing contract), but fall
      // back to reference identity when it doesn't - Source Item rows have no
      // lineItem, and `undefined === undefined` would otherwise match whichever
      // lineItem-less row happens to be found first, not necessarily this one.
      const isSelf = row.lineItem
        ? this.rows[i].lineItem?.id === row.lineItem.id
        : this.rows[i] === row;
      if (!aboveRow && isSelf) {
        aboveRow = true;
      } else if (aboveRow && this.rows[i].indentation < row.indentation) {
        return this.hiddenRowIndices.has(i);
      }
    }
    return false;
  }

  isRowVisible(row: DataViewRow): boolean {
    if (this.isSourceStatement) {
      return true;
    }
    if (row.rowFormat && row.rowFormat.header && row.indentation === -1) {
      return true;
    }
    if (row.rowFormat && row.rowFormat.header) {
      return true;
    }
    // Source-item rows (shown via the Source Items toggle) aren't tied to a template lineItem
    // and so have no section grouping to check - always show them.
    if (!row.lineItem) {
      return true;
    }

    const parentSectionId = this.lineItemIdToParentSectionIdMapping[row.lineItem.id];
    if (parentSectionId !== undefined && parentSectionId !== -1) {
      const parentState = this.sectionDrawerState[parentSectionId];
      return parentState === true;
    }

    return true;
  }

  // Whether row belongs to a currently-expanded section drawer. Source-item rows
  // aren't tied to a template lineItem, so they have no section to check - always show them.
  isRowSectionExpanded(row: DataViewRow): boolean {
    if (!row.lineItem) {
      return true;
    }
    return !!this.sectionDrawerState[this.lineItemIdToParentSectionIdMapping[row.lineItem.id]];
  }

  toggleColumnContextMenu(idx: number, event: MouseEvent): void {
    if (this.contextMenuOpenColumnIdx !== idx) {
      this.contextMenuOpenColumnIdx = idx;
      const th = (event.currentTarget as HTMLElement).closest('th');
      if (th) {
        const rect = th.getBoundingClientRect();
        this.contextMenuPosition = { top: rect.bottom, left: rect.left };
      }
    } else {
      this.contextMenuOpenColumnIdx = -1;
      this.selectedColumn = -1;
    }
  }

  // If current column has comments, will show the comment icon
  columnHasComments(column: DataViewColumn): boolean {
    return !!column.sourceComments?.length;
  }

  // Opens the comment modal
  openColumnComments(column: DataViewColumn): void {
    this.openCommentView.emit(column);
  }

  handleCellCommentView(cellCommentData: {cellInformation: any, cellTitle: string, cellStatementDate: string}): void {
    this.openCellCommentView.emit(cellCommentData);
  }

  /**
   * Pre-compute which cells should be highlighted.
   * Called once when insight-related inputs change.
   * Uses objects for direct template access without method calls.
   */
  private computeHighlightedCells(): void {
    // Reset maps - create new objects to trigger change detection
    this.highlightedCellMap = {};
    this.selectedCellMap = {};
    
    if (this.insightHighlightColumnIndex === -1 || !this.rows) {
      return;
    }
    
    const colIdx = this.insightHighlightColumnIndex;
    let selectedCellKey: string = null;
    
    // Find selected cell
    if (this.insightSelectedRowRef) {
      for (let rowIdx = 0; rowIdx < this.rows.length; rowIdx++) {
        const row = this.rows[rowIdx];
        if (row && row.ref && this.insightSelectedRowRef === row.ref) {
          selectedCellKey = `${rowIdx}-${colIdx}`;
          this.selectedCellMap[selectedCellKey] = true;
          break;
        }
      }
    }
    
    // Find highlighted cells (contributing cells, excluding the selected cell)
    if (this.insightHighlightRefs && this.insightHighlightRefs.size > 0) {
      for (let rowIdx = 0; rowIdx < this.rows.length; rowIdx++) {
        const row = this.rows[rowIdx];
        if (row && row.ref && this.insightHighlightRefs.has(row.ref)) {
          const cellKey = `${rowIdx}-${colIdx}`;
          // Don't add to highlighted if it's the selected cell
          if (cellKey !== selectedCellKey) {
            this.highlightedCellMap[cellKey] = true;
          }
        }
      }
    }
  }
}
